/* eslint-disable react-hooks/rules-of-hooks */
import { useRequest } from 'ahooks';
import { Formik, FormikConfig, FormikProps } from 'formik';
import { ReactNode, useEffect, useState } from 'react';
import {
  createErpOrder,
  getErpOrder,
  getJstTraceChannel,
  getQyTraceChannel,
  reviewErpOrder,
} from '../api';
import FormField from 'components/FormField';
import { Button, Divider, Form, Input, message, Modal, ModalProps, Radio, Select } from 'antd';
import { useTranslate } from '@tolgee/react';
import { CopyOutlined } from '@ant-design/icons';
import copyText from 'utils/copyText';
import { find } from 'lodash-es';
import { Flex, FV } from './share';
import ItemInfo from './ItemInfo';

interface ErpOrderFormProps extends FormikConfig<FV> {
  disabled?: boolean;
  storeId: number;
  itemOffsale?: boolean;
  wms: ERP.Wm[];
  item: Pick<Conversation.Trade.Product, 'externalItemId' | 'itemPic' | 'itemName'>;
  skuInfo: Pick<Conversation.ProductSKUInfo, 'sku' | 'skuId' | 'sellerSku'>[];
  children: (form: ReactNode, props: FormikProps<FV>) => ReactNode;
}

function ErpOrderForm({
  disabled,
  storeId,
  itemOffsale,
  wms,
  item,
  skuInfo,
  children,
  initialValues,
  onSubmit,
  ...otherProps
}: ErpOrderFormProps) {
  const { t } = useTranslate();

  const [newDelivery, setNewDelivery] = useState(false);

  useEffect(() => {
    const { receiverName, erpType } = initialValues;
    if (erpType === 'JST') {
      setNewDelivery(Boolean(receiverName));
    }
  }, [initialValues]);

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={(values, helpers) => {
        if (!newDelivery && values.erpType === 'JST') {
          return onSubmit(
            {
              ...values,
              receiverName: '',
              phone: '',
              postCode: '',
              province: '',
              city: '',
              district: '',
              address: '',
            },
            helpers,
          );
        }
        return onSubmit(
          {
            ...values,
            skus: values.skus
              ?.filter((_) => _)
              .map(({ subSkus, skuQuantity, ...others }) => {
                return {
                  skuQuantity: subSkus.length > 0 ? 0 : skuQuantity,
                  subSkus,
                  ...others,
                };
              }),
          },
          helpers,
        );
      }}
      {...otherProps}
    >
      {(formProps) => {
        const { values, setValues } = formProps;

        const { erpType, warehouseName, traceChannel } = values;

        const qyTraceChannelState = useRequest(
          () =>
            getQyTraceChannel({
              storeId,
              warehouseName,
            }),
          {
            ready: erpType === 'QY' && Boolean(warehouseName),
            refreshDeps: [warehouseName],
            onSuccess: (data) => {
              if (data.every((_) => _.code !== traceChannel?.code)) {
                setValues({
                  ...values,
                  traceChannel: undefined,
                });
              }
            },
          },
        );
        const jstTraceChannelState = useRequest(() => getJstTraceChannel({ storeId }), {
          ready: erpType === 'JST',
          onSuccess: (data) => {
            if (
              data.every(({ channel, channelType }) => {
                return (
                  channelType !== values.channelType ||
                  channel.every(({ code }) => code !== traceChannel?.code)
                );
              })
            ) {
              setValues({
                ...values,
                channelType: undefined,
                traceChannel: undefined,
              });
            }
          },
        });
        const jstChannels = jstTraceChannelState.data?.find(
          ({ channelType }) => channelType === values.channelType,
        )?.channel;

        const form = (
          <Form layout="vertical" colon={false}>
            <ItemInfo
              disabled={disabled}
              storeId={storeId}
              itemOffsale={itemOffsale}
              item={item}
              skuInfo={skuInfo}
            />
            <FormField<FV['warehouseCode']>
              name="warehouseCode"
              required
              label={t('发货仓')}
              validate={(value) =>
                value ? undefined : t('department.请选择x', { x: t('发货仓') })
              }
            >
              {({ field: { value, onBlur } }) => {
                return (
                  <Flex items={[1, 1]}>
                    <Select
                      disabled={disabled}
                      placeholder={t('department.请选择x', { x: t('发货仓') })}
                      value={value}
                      onChange={(code) => {
                        const target = wms.find(
                          (_) => (erpType === 'QY' ? _.name : `${_.warehouseId}`) === code,
                        );
                        if (target) {
                          setValues({
                            ...values,
                            warehouseCode: code,
                            warehouseName: target.name,
                          });
                        }
                      }}
                      onBlur={onBlur}
                      options={wms.map(({ warehouseId, name }) => ({
                        value: erpType === 'QY' ? name : `${warehouseId}`,
                        label: name,
                      }))}
                    />
                    <span />
                  </Flex>
                );
              }}
            </FormField>
            <Flex items={[1, 1]}>
              <FormField<FV['traceChannel']>
                name="traceChannel"
                required={erpType === 'QY'}
                label={erpType === 'QY' ? t('发货物流') : t('物流渠道')}
                description={erpType === 'QY' ? t('补发订单请选择线下物流进行发货') : undefined}
                validate={(value) =>
                  value || erpType !== 'QY'
                    ? undefined
                    : t('department.请选择x', { x: t('物流渠道') })
                }
              >
                {({ field: { value, onChange, onBlur } }) => {
                  const channels = erpType === 'JST' ? jstChannels : qyTraceChannelState.data;
                  const channelSelect = (
                    <Select
                      allowClear
                      showSearch
                      disabled={erpType === 'JST' ? disabled || !values.channelType : disabled}
                      loading={
                        erpType === 'JST'
                          ? jstTraceChannelState.loading
                          : qyTraceChannelState.loading
                      }
                      dropdownMatchSelectWidth={false}
                      placeholder={t('department.请选择x', { x: t('物流渠道') })}
                      value={value?.code}
                      onChange={(value) => {
                        if (!value) {
                          onChange(undefined);
                        } else {
                          const target = channels?.find((_) => _.code === value);
                          if (target) {
                            onChange(target);
                          }
                        }
                      }}
                      onBlur={onBlur}
                      options={channels?.map(({ code, name }) => ({
                        value: code,
                        label: name,
                      }))}
                      optionFilterProp="label"
                    />
                  );
                  if (erpType !== 'JST') {
                    return channelSelect;
                  }
                  const jstChannelSelect = (
                    <Select
                      style={{ marginBottom: 16 }}
                      allowClear
                      showSearch
                      disabled={disabled}
                      loading={jstTraceChannelState.loading}
                      dropdownMatchSelectWidth={false}
                      placeholder={t('department.请选择x', { x: t('物流商') })}
                      value={values.channelType}
                      onChange={(value) => {
                        setValues({
                          ...values,
                          channelType: value,
                          traceChannel: undefined,
                        });
                      }}
                      options={jstTraceChannelState.data?.map(({ name, channelType }) => ({
                        value: channelType,
                        label: name,
                      }))}
                    />
                  );
                  return (
                    <div>
                      {jstChannelSelect}
                      {channelSelect}
                    </div>
                  );
                }}
              </FormField>
              <span />
            </Flex>
            <section
              style={{
                padding: '12px',
                background: '#F7F9FA',
              }}
            >
              <h4>{t('收货信息')}</h4>
              {erpType === 'JST' && (
                <div>
                  <Radio
                    disabled={disabled}
                    checked={!newDelivery}
                    onChange={({ target: { checked } }) => checked && setNewDelivery(false)}
                  >
                    {t('使用关联订单收货信息')}
                  </Radio>
                  <Radio
                    disabled={disabled}
                    checked={newDelivery}
                    onChange={({ target: { checked } }) => checked && setNewDelivery(true)}
                  >
                    {t('使用新收货信息')}
                  </Radio>
                </div>
              )}
              {erpType === 'JST' && newDelivery && <Divider type="horizontal" />}
              {(erpType !== 'JST' || newDelivery) && (
                <>
                  <Flex items={[1, 1, 1]}>
                    <FormField<FV['receiverName']>
                      name="receiverName"
                      required
                      label={t('收货人姓名')}
                      validate={(value) =>
                        value ? undefined : t('department.请输入x', { x: t('收货人姓名') })
                      }
                    >
                      {({ field }) => {
                        return (
                          <Input
                            showCount
                            disabled={disabled}
                            maxLength={40}
                            placeholder={t('收货人姓名')}
                            {...field}
                          />
                        );
                      }}
                    </FormField>
                    <FormField<FV['phone']>
                      name="phone"
                      required
                      label={t('手机号码')}
                      validate={(value) =>
                        value ? undefined : t('department.请输入x', { x: t('手机号码') })
                      }
                    >
                      {({ field }) => {
                        return <Input disabled={disabled} placeholder={t('手机号码')} {...field} />;
                      }}
                    </FormField>
                    {erpType === 'QY' ? (
                      <FormField<FV['postCode']>
                        name="postCode"
                        required
                        label={t('邮编')}
                        validate={(value) =>
                          value ? undefined : t('department.请输入x', { x: t('邮编') })
                        }
                      >
                        {({ field }) => {
                          return <Input disabled={disabled} placeholder={t('邮编')} {...field} />;
                        }}
                      </FormField>
                    ) : (
                      <div />
                    )}
                  </Flex>
                  <Flex items={[1, 1, 1]}>
                    <FormField<FV['province']>
                      name="province"
                      required
                      label={t('地址信息')}
                      validate={(value) =>
                        value ? undefined : t('department.请输入x', { x: t('省/州') })
                      }
                    >
                      {({ field }) => {
                        return <Input disabled={disabled} placeholder={t('省/州')} {...field} />;
                      }}
                    </FormField>
                    <FormField<FV['city']>
                      name="city"
                      label=" "
                      validate={(value) =>
                        value ? undefined : t('department.请输入x', { x: t('城市') })
                      }
                    >
                      {({ field }) => {
                        return <Input disabled={disabled} placeholder={t('城市')} {...field} />;
                      }}
                    </FormField>
                    <FormField<FV['district']>
                      name="district"
                      label=" "
                      validate={(value) =>
                        value ? undefined : t('department.请输入x', { x: t('区/县') })
                      }
                    >
                      {({ field }) => {
                        return <Input disabled={disabled} placeholder={t('区/县')} {...field} />;
                      }}
                    </FormField>
                  </Flex>
                  <FormField<FV['address']>
                    name="address"
                    required
                    label={t('详细地址')}
                    validate={(value) =>
                      value ? undefined : t('department.请输入x', { x: t('详细地址') })
                    }
                  >
                    {({ field }) => {
                      return (
                        <Input.TextArea
                          disabled={disabled}
                          placeholder={t('department.请输入x', { x: t('详细地址') })}
                          {...field}
                        />
                      );
                    }}
                  </FormField>
                </>
              )}
            </section>
          </Form>
        );

        return children(form, {
          ...formProps,
          handleSubmit: (e) => {
            if (
              values.skus
                ?.filter((_) => _)
                .some(({ skuId }, index) => {
                  return values.skus?.slice(0, index).some((_) => _.skuId === skuId);
                })
            ) {
              return message.error(t('ERP系统商品编码重复'));
            }
            formProps.validateForm().then((errors) => {
              if (errors.skus?.length) {
                const target = (errors.skus as any as string[]).find((_) => typeof _ === 'string');
                if (target) {
                  message.error(target);
                }
              }
            });
            formProps.handleSubmit(e);
          },
        });
      }}
    </Formik>
  );
}

export type ErpOrderModalProps = ModalProps &
  Pick<ErpOrderFormProps, 'storeId' | 'itemOffsale' | 'wms' | 'skuInfo' | 'initialValues'> & {
    mode: 'create' | 'review' | 'view' | 'createReview';
    item: Pick<Conversation.Trade.Product, 'externalItemId' | 'itemPic' | 'itemName'>;
    onUpdate: () => void;
    source?: string; // 用于区分创建补发单的来源
  };

export function ErpOrderModal({
  mode,
  item,
  storeId,
  itemOffsale,
  wms,
  skuInfo,
  initialValues,
  onUpdate,
  source,
  ...otherProps
}: ErpOrderModalProps) {
  const { t } = useTranslate();

  return (
    <ErpOrderForm
      disabled={mode === 'review' || mode === 'view'}
      storeId={storeId}
      itemOffsale={itemOffsale}
      wms={wms}
      item={item}
      skuInfo={skuInfo}
      initialValues={initialValues}
      onSubmit={(values) => {
        return (
          mode === 'create'
            ? createErpOrder({ erpOrder: { ...values, source } })
            : mode === 'createReview'
            ? createErpOrder({ erpOrder: { ...values, source } }).then(() => {
                return getErpOrder(values.todoId || '')
                  .then((res) => {
                    const targetOrder = find(res.orders, (o) => o.skuId === values.skuId);
                    if (!targetOrder) {
                      return Promise.reject(new Error('no erp order'));
                    }
                    const { todoId, warehouseCode, warehouseName, erpId } = targetOrder;
                    return reviewErpOrder({ todoId, warehouseCode, warehouseName, erpId });
                  })
                  .catch((err) => {
                    onUpdate();
                    return Promise.reject(err);
                  });
              })
            : mode === 'review'
            ? reviewErpOrder({
                erpId: values?.erpId || '',
                todoId: values.todoId || '',
                warehouseName: values.warehouseName || '',
                warehouseCode: values.warehouseCode || '',
              })
            : Promise.reject(new Error('invalid mode'))
        )
          .then(() => {
            message.success(t('phrase.保存成功'));
            onUpdate();
          })
          .catch((e) => {
            message.error(e?.response?.data?.msg || t('phrase.保存失败，请稍后再试'));
          });
      }}
    >
      {(form, { values: { erpTradeId }, isSubmitting, handleSubmit }) => {
        return (
          <Modal
            width={680}
            title={
              {
                create: t('创建补发单'),
                review: t('审核补发单'),
                view: t('查看补发单'),
                createReview: t('创建补发单'),
              }[mode]
            }
            footer={
              mode === 'view' ? (
                false
              ) : (
                <div className="flex items-center justify-end">
                  {mode === 'review' && (
                    <div className="mr-4">
                      {t('补发单号')}：{erpTradeId}
                      <CopyOutlined
                        onClick={() => {
                          copyText(erpTradeId || '').then(() => {
                            message.success(t('todo.已复制'));
                          });
                        }}
                      />
                    </div>
                  )}
                  <Button onClick={otherProps.onCancel}>{t('取消')}</Button>
                  <Button type="primary" loading={isSubmitting} onClick={() => handleSubmit()}>
                    {mode === 'create' && t('创建')}
                    {mode === 'review' && t('确认审核通过')}
                    {mode === 'createReview' && t('创建并审核')}
                  </Button>
                </div>
              )
            }
            {...otherProps}
          >
            {form}
          </Modal>
        );
      }}
    </ErpOrderForm>
  );
}

export default ErpOrderForm;
